// LiquidityEcosystem · "Empowering Trading With Technology".
// Visualises the OFinancial → LD4 → Tier-1 banks order flow as a
// connected diagram with animated pulse lines, then surfaces a LIVE
// TERMINAL that streams realistic order-routing log lines, and a
// NOC-style TELEMETRY DASHBOARD with 6 live tiles · so the section
// finally reads like real infrastructure, not marketing copy.
import { useRef, useState, useEffect } from "react";
import { motion, useInView, animate } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  User, Server, Building, Activity, Zap,
} from "lucide-react";
import TelemetryDashboard from "@/components/TelemetryDashboard";

// Hook · counts up to a target value while in view.
function useCountUp(to, { start = false, duration = 2.0, from = 0 } = {}) {
  const [v, setV] = useState(from);
  useEffect(() => {
    if (!start) return;
    const c = animate(from, to, { duration, ease: [0.22, 1, 0.36, 1], onUpdate: setV });
    return () => c.stop();
  }, [start, to, duration, from]);
  return v;
}

function HubNode({ Icon, label, sub, accent, delay = 0 }) {
  // No square-bordered tile · just the icon floating inside a subtle
  // radial halo. Reads as a node on the wire rather than a chip
  // mounted on a PCB. Accent node gets a slow pulse ring (also no
  // square border) to mark it as the smart router.
  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.92 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      whileHover={{ y: -3, scale: 1.04 }}
      viewport={{ once: true, margin: "-15% 0px" }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
      className="relative flex flex-col items-center text-center shrink-0"
    >
      <div className="relative w-12 h-12 sm:w-14 sm:h-14 mb-2 grid place-items-center">
        {/* Soft radial halo · stronger on the accent (router) node */}
        <div
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{
            background: `radial-gradient(circle, color-mix(in srgb, var(--brand) ${accent ? 28 : 14}%, transparent) 0%, transparent 70%)`,
            filter: "blur(2px)",
          }}
        />
        {/* Pulsing accent ring · circular, NOT square — only on router */}
        {accent && (
          <motion.div
            aria-hidden
            className="absolute inset-0 rounded-full"
            style={{ border: "1px solid color-mix(in srgb, var(--brand) 55%, transparent)" }}
            animate={{ scale: [1, 1.25, 1], opacity: [0.55, 0, 0.55] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
          />
        )}
        <Icon
          size={accent ? 24 : 22}
          strokeWidth={1.6}
          style={{ color: "var(--brand)", position: "relative", zIndex: 1 }}
        />
      </div>
      <div className="font-display text-[12px] font-semibold text-text-1">{label}</div>
      {sub && <div className="font-mono text-[8.5px] uppercase tracking-[0.16em] text-text-3 mt-0.5">{sub}</div>}
    </motion.div>
  );
}

/* MiniConnector · simple animated arrow used in the mobile Z-pattern
   2×2 grid. Three direction variants (right · down · left). Pulses a
   small dot along the line so the diagram still feels alive without
   the heavy fibre-optic SVG used on desktop. */
function MiniConnector({ dir = "right" }) {
  if (dir === "down") {
    return (
      <div className="relative w-1 h-12 mx-auto" aria-hidden>
        <div
          className="absolute inset-x-0 inset-y-0"
          style={{
            background: "repeating-linear-gradient(180deg, color-mix(in srgb, var(--brand) 55%, transparent) 0 4px, transparent 4px 9px)",
          }}
        />
        <motion.span
          className="absolute -left-[3px] w-[7px] h-[7px] rounded-full"
          style={{ background: "var(--brand)", boxShadow: "0 0 6px var(--brand)" }}
          animate={{ top: ["0%", "100%"], opacity: [0, 1, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>
    );
  }
  const reverse = dir === "left";
  return (
    <div className="relative h-1 w-12 my-auto" aria-hidden>
      <div
        className="absolute inset-0"
        style={{
          background: "repeating-linear-gradient(90deg, color-mix(in srgb, var(--brand) 55%, transparent) 0 4px, transparent 4px 9px)",
        }}
      />
      <motion.span
        className="absolute -top-[3px] w-[7px] h-[7px] rounded-full"
        style={{ background: "var(--brand)", boxShadow: "0 0 6px var(--brand)" }}
        animate={
          reverse
            ? { left: ["100%", "0%"], opacity: [0, 1, 0] }
            : { left: ["0%", "100%"], opacity: [0, 1, 0] }
        }
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
}

function PulseConnector({ label }) {
  // Realistic fibre-optic cable connector · SVG with twisted-pair
  // strands, an inner light core that travels left-to-right, and two
  // discrete data packets that slide along the cable. Used on desktop
  // only — the mobile Z-pattern uses MiniConnector instead.
  return (
    <div className="relative flex-1 mx-2 sm:mx-4 self-center min-w-[40px] h-7" aria-hidden>
      <svg viewBox="0 0 200 28" preserveAspectRatio="none" className="absolute inset-0 w-full h-full overflow-visible">
        <defs>
          <linearGradient id="cable-core" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"  stopColor="color-mix(in srgb, var(--brand) 0%, transparent)" />
            <stop offset="50%" stopColor="color-mix(in srgb, var(--brand) 85%, transparent)" />
            <stop offset="100%" stopColor="color-mix(in srgb, var(--brand) 0%, transparent)" />
          </linearGradient>
          <linearGradient id="cable-sheath" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor="color-mix(in srgb, var(--brand) 22%, transparent)" />
            <stop offset="50%" stopColor="color-mix(in srgb, var(--brand) 38%, transparent)" />
            <stop offset="100%" stopColor="color-mix(in srgb, var(--brand) 14%, transparent)" />
          </linearGradient>
        </defs>

        {/* Cable sheath · outer rounded strip with a soft gradient so it
            reads as physical material rather than a hairline */}
        <rect x="0" y="11" width="200" height="6" rx="3" fill="url(#cable-sheath)" />
        {/* Inner hair-rule highlight · gives the cable a glossy top */}
        <line x1="0" y1="12.5" x2="200" y2="12.5" stroke="color-mix(in srgb, var(--brand) 35%, transparent)" strokeWidth="0.4" />
        {/* Light core · animated bright band that travels through the
            cable representing a continuous photonic carrier wave */}
        <motion.rect
          x="0" y="13" width="56" height="2" rx="1"
          fill="url(#cable-core)"
          animate={{ x: [-60, 204] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: "linear" }}
        />
        {/* Discrete data packets · two small bright pellets following
            each other so the cable looks like it's actually carrying
            individual transmissions. We animate via translateX on a
            wrapping <motion.g> rather than the SVG cx attribute,
            because framer-motion intermittently emits an undefined
            cx during keyframe interpolation which triggered React
            "<circle> attribute cx: Expected length, undefined" warnings. */}
        <motion.g
          animate={{ x: [0, 212] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "linear" }}
        >
          <circle
            cx={-6} cy="14" r="2.4"
            fill="var(--brand)"
            style={{ filter: "drop-shadow(0 0 3px color-mix(in srgb, var(--brand) 70%, transparent))" }}
          />
        </motion.g>
        <motion.g
          animate={{ x: [0, 212] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "linear", delay: 0.9 }}
        >
          <circle
            cx={-6} cy="14" r="1.6"
            fill="color-mix(in srgb, var(--brand) 70%, transparent)"
          />
        </motion.g>
        {/* End-caps · small connector ferrules on each side of the
            cable, communicating "plugged-in" hardware */}
        <rect x="-1" y="9.5" width="4" height="9" rx="1" fill="color-mix(in srgb, var(--brand) 48%, transparent)" />
        <rect x="197" y="9.5" width="4" height="9" rx="1" fill="color-mix(in srgb, var(--brand) 48%, transparent)" />
      </svg>

      {/* Label · plain mono text floating ABOVE the cable (no pill /
          no border) so it reads as an infrastructure annotation, not
          a UI chip. Bumped to 10px with normal tracking so the
          letters don't appear warped at small size. */}
      {label && (
        <div className="absolute left-1/2 -translate-x-1/2 -top-4 pointer-events-none">
          <span
            className="font-mono text-[9.5px] uppercase tracking-[0.14em] whitespace-nowrap"
            style={{ color: "color-mix(in srgb, var(--text-3) 92%, transparent)" }}
          >
            {label}
          </span>
        </div>
      )}
    </div>
  );
}

export default function LiquidityEcosystem() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px" });

  // Live, counting metrics · animate from 0 to their target when in view.
  const latency = useCountUp(12, { start: inView, duration: 1.8, from: 18 });
  const orders = useCountUp(847232, { start: inView, duration: 2.4 });
  const uptime = useCountUp(99.97, { start: inView, duration: 2.0 });
  const lps = useCountUp(14, { start: inView, duration: 1.4 });

  // Mini-jitter on latency to feel "live" after the count-up completes.
  const [liveLatency, setLiveLatency] = useState(12);
  useEffect(() => {
    if (!inView) return;
    const t = setInterval(() => setLiveLatency(11 + Math.round(Math.random() * 3)), 1800);
    return () => clearInterval(t);
  }, [inView]);
  const showLive = Math.round(latency) === 12;

  return (
    <section ref={ref} data-testid="liquidity-ecosystem" className="relative py-16 lg:py-20 overflow-hidden">
      {/* Background flair · softer */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[640px] h-[400px] rounded-full" style={{ background: "radial-gradient(circle, color-mix(in srgb, var(--brand) 6%, transparent), transparent 60%)" }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-8 lg:mb-10">
          {/* Live status pill replaces the static pill · pulses subtly */}
          <motion.div
            className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full font-mono text-[12px] uppercase tracking-[0.22em]"
            style={{
              background: "color-mix(in srgb, var(--surface) 92%, transparent)",
              color: "var(--text-2)",
              border: "1px solid color-mix(in srgb, var(--brand) 28%, transparent)",
            }}
            initial={{ opacity: 0, y: -6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <motion.span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: "#54d97c", boxShadow: "0 0 6px #54d97c" }}
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.4, repeat: Infinity }}
            />
            {t("liquidity.live", { defaultValue: "Live · {{count}} orders routed today", count: Math.round(orders).toLocaleString() })}
          </motion.div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-[-0.025em] leading-[1.05] text-text-1">
            {t("liquidity.h1", "Empowering trading with")} <span className="text-gradient-brand font-semibold">{t("liquidity.hAccent", "technology")}</span>
          </h2>
          <p className="text-text-3 mt-5 max-w-3xl mx-auto text-[15px] leading-relaxed">
            {t("liquidity.sub", "Your trades route from OFinancial through our LD4-colocated bridge directly to a pool of tier-1 bank liquidity providers · direct routing, no synthetic price feeds, no last-look interference.")}
          </p>
        </div>

        {/* ===== Flow diagram ===== */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="rounded-2xl p-5 sm:p-7 lg:p-9 mb-8 relative overflow-hidden"
          style={{
            borderColor: "color-mix(in srgb, var(--brand) 10%, var(--line))",
            borderWidth: "1px",
            borderStyle: "solid",
            background: "linear-gradient(180deg, color-mix(in srgb, var(--surface) 55%, transparent), color-mix(in srgb, var(--surface-2) 55%, transparent))",
            backdropFilter: "blur(6px)",
          }}
          data-testid="liquidity-flow-diagram"
        >
          {/* ===== Mobile · Z-pattern 2×2 grid =====
              On phones we abandon the linear flow (which is way too
              tall) and lay the 4 nodes out as a square. Arrows snake
              top-row (left→right), down-arrow on the right, then
              bottom-row (right→left) — a "Z" through the diagram. */}
          <div className="lg:hidden grid grid-cols-[1fr_auto_1fr] gap-y-3 items-center">
            {/* Row 1 · You ─→ OFinancial */}
            <HubNode Icon={User} label={t("liquidity.you", "You")} sub={t("liquidity.theTrader", "The Trader")} delay={0.05} />
            <MiniConnector dir="right" />
            <HubNode Icon={Zap} label="OFinancial" sub={t("liquidity.smartRouter", "Smart router")} accent delay={0.15} />

            {/* Row 2 · spacer · down-arrow under the right column · spacer */}
            <span />
            <span />
            <MiniConnector dir="down" />

            {/* Row 3 · Tier-1 Banks ←─ LD4 Bridge */}
            <HubNode Icon={Building} label={t("liquidity.tier1", "Tier-1 Banks")} sub={t("liquidity.lpPool", "LP pool")} delay={0.35} />
            <MiniConnector dir="left" />
            <HubNode Icon={Server} label={t("liquidity.ld4", "LD4 Bridge")} sub={t("liquidity.ld4Sub", "London · colo")} delay={0.25} />
          </div>

          {/* ===== Desktop · horizontal flow with cable connectors ===== */}
          <div className="hidden lg:flex items-center gap-0">
            <HubNode Icon={User} label={t("liquidity.you", "You")} sub={t("liquidity.theTrader", "The Trader")} delay={0.05} />
            <div className="flex flex-1 items-stretch">
              <PulseConnector label={t("liquidity.placeOrder", "Place order")} />
            </div>
            <HubNode Icon={Zap} label="OFinancial" sub={t("liquidity.smartRouter", "Smart router")} accent delay={0.15} />
            <div className="flex flex-1 items-stretch">
              <PulseConnector label={t("liquidity.aggregate", "Aggregate")} />
            </div>
            <HubNode Icon={Server} label={t("liquidity.ld4", "LD4 Bridge")} sub={t("liquidity.ld4Sub", "London · colo")} delay={0.25} />
            <div className="flex flex-1 items-stretch">
              <PulseConnector label={t("liquidity.execute", "Execute")} />
            </div>
            <HubNode Icon={Building} label={t("liquidity.tier1", "Tier-1 Banks")} sub={t("liquidity.lpPool", "LP pool")} delay={0.35} />
          </div>

          {/* Latency footer · NOW with animated count-up + live jitter */}
          <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-7 pt-6 border-t" style={{ borderColor: "var(--line)" }}>
            <Stat
              label={t("liquidity.avgLatency", "Avg latency")}
              value={`${showLive ? liveLatency : Math.round(latency)} ms`}
              live
            />
            <Stat label={t("liquidity.lpPool", "LP pool")} value={t("liquidity.lpUnit", { defaultValue: "{{n}} banks", n: Math.round(lps) })} />
            <Stat label={t("liquidity.uptime", "Uptime")} value={`${uptime.toFixed(2)}%`} />
          </div>
        </motion.div>

        {/* ===== TELEMETRY DASHBOARD · 6 live tiles + integrated
            LiveTerminal in the right column (col 4 row-span-2 on lg+,
            full-width strip on mobile). One balanced rectangle
            instead of two stacked blocks. ===== */}
        <TelemetryDashboard />
      </div>
    </section>
  );
}

function Stat({ label, value, live = false }) {
  return (
    <div className="text-center min-w-0">
      <div className="font-display text-lg sm:text-2xl font-semibold text-text-1 tabular-nums leading-none whitespace-nowrap flex items-center justify-center gap-1.5">
        {value}
        {live && (
          <motion.span
            className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
            style={{ background: "#54d97c" }}
            animate={{ opacity: [1, 0.25, 1] }}
            transition={{ duration: 1.2, repeat: Infinity }}
          />
        )}
      </div>
      <div className="text-text-3 mt-1.5 leading-tight text-[12px] sm:font-mono sm:text-[12px] sm:uppercase sm:tracking-[0.2em]">{label}</div>
    </div>
  );
}
